/**
 * 子パターン（#2967）の camelCase ⇄ snake_case 変換。
 *
 * Firestore は snake_case、アプリ内は camelCase という既存の使い分けに合わせる。
 * item_list に対する itemListConverter.ts と同じ役割を、item_overrides に対して担う。
 *
 * 全保存経路・全読込経路がこの関数を通るため、ここで正規化しておけば
 * Firestore に不正な形の上書き定義が残らない。
 */

import type { ItemOverrideType } from '../../types/subPattern';

/** Firestore 保存用の上書き定義（snake_case） */
export type ItemOverrideSnake = {
  item_id: string;
  extraction_rule: string;
  fixed_value: string;
  item_name: string;
  /** AI読取のON/OFFの上書き（#2967）。undefined なら親の設定をそのまま使う */
  is_ocr_target?: boolean;
};

/**
 * 上書き定義を Firestore 用の snake_case に変換する。
 *
 * 取得ルール・固定値がどちらも空の項目は「上書きするものが何もない」ため取り除く。
 * 残しても適用時に無視されるだけだが、判定プロンプトや一覧の「N項目を上書き」の
 * 件数がずれるため、保存の時点で落としておく。
 */
export const convertItemOverridesToSnakeCase = (
  overrides: ItemOverrideType[],
): ItemOverrideSnake[] =>
  overrides
    .map(override => ({
      item_id: override.itemId,
      extraction_rule: (override.extractionRule ?? '').trim(),
      fixed_value: (override.fixedValue ?? '').trim(),
      item_name: (override.itemName ?? '').trim(),
      // undefined を Firestore に書けないため、指定があるときだけキーを持たせる
      ...(override.isOcrTarget !== undefined ? { is_ocr_target: override.isOcrTarget } : {}),
    }))
    .filter(override => override.item_id !== '')
    .filter(
      override =>
        override.extraction_rule !== '' ||
        override.fixed_value !== '' ||
        override.item_name !== '' ||
        override.is_ocr_target !== undefined,
    );

/**
 * Firestore の snake_case を camelCase に変換する。
 *
 * 欠損フィールドは空文字で補う。空文字は「上書きしない」の意味になるため、
 * 古い形式のデータが混ざっても親の設定がそのまま使われるだけで壊れない。
 */
export const convertItemOverridesToCamelCase = (
  overrides: ItemOverrideSnake[] | undefined,
): ItemOverrideType[] =>
  (overrides ?? [])
    .map(override => ({
      itemId: override.item_id ?? '',
      extractionRule: override.extraction_rule ?? '',
      fixedValue: override.fixed_value ?? '',
      itemName: override.item_name ?? '',
      isOcrTarget: override.is_ocr_target,
    }))
    .filter(override => override.itemId !== '');

/**
 * 実際に上書きされる項目の件数。パターン一覧の「N項目を上書き」表示に使う。
 * 保存時に空の上書きは除去されるが、既存データにも対応できるよう改めて数える。
 */
export const countActiveOverrides = (overrides: ItemOverrideType[]): number =>
  overrides.filter(
    override =>
      (override.extractionRule ?? '') !== '' ||
      (override.fixedValue ?? '') !== '' ||
      (override.itemName ?? '') !== '' ||
      override.isOcrTarget !== undefined,
  ).length;
